import { useCallback, useState } from "react";
import type { z } from "zod";

/**
 * Errores POR CAMPO contra el mismo esquema Zod que valida el servidor.
 *
 * ── Por qué existe (Anahí, 1-sep) ────────────────────────────────────
 * «Debes agregar validaciones en todos los campos, inputs, selects, etc.
 * porque no se puede hacer eso». Y podía: la caja del teléfono aceptaba
 * veinte dígitos y no decía nada.
 *
 * Lo que NO estaba roto es la seguridad. El servidor valida con Zod cada
 * cuerpo que entra y siempre rechazó esos veinte dígitos; Mongoose
 * parametriza las consultas y las pocas expresiones regulares que tocan
 * entrada de gente van escapadas. Nada de lo que se teclee aquí llega
 * crudo a la base — eso ya estaba resuelto y NO depende del navegador,
 * que es lo primero que un atacante se salta.
 *
 * Lo que estaba roto es OTRA cosa, y es la que arregla este archivo:
 * quien teclea no se entera de que va mal hasta que pulsa Guardar, y
 * entonces recibe UN error para un formulario de cuatro campos. Este
 * hook pone el error debajo del campo, en el momento en que se sale de
 * él, y lo quita en cuanto deja de serlo.
 *
 * ── Por qué contra el esquema y no con reglas propias ────────────────
 * Reescribir "el teléfono empieza con +" en la pantalla crearía una
 * segunda definición de lo válido, que empieza igual que la del servidor
 * y se separa en el primer arreglo que toque una sola. Aquí se pasa el
 * MISMO esquema: el mensaje que se lee bajo el campo es, palabra por
 * palabra, el que devolvería el API.
 *
 * ── Los tres momentos ────────────────────────────────────────────────
 * · `revisar(campo, valores)` — al SALIR del campo. Solo marca ese: que
 *   escribir el nombre pinte de rojo el teléfono todavía vacío es
 *   regañar a alguien por algo que aún no ha hecho.
 * · `limpiar(campo)` — al ESCRIBIR. El error se va con la primera tecla;
 *   dejarlo puesto mientras se corrige es discutir con quien ya te hizo
 *   caso.
 * · `validar(valores)` — al ENVIAR. Marca todos de golpe y devuelve los
 *   datos ya parseados, o `null`.
 */
export interface FieldErrors<TValues> {
  /** Lo que hay que enseñar debajo de cada campo, si algo. */
  errors: Partial<Record<keyof TValues & string, string>>;
  /** Al salir de un campo: valida y marca SOLO ese. */
  revisar: (campo: keyof TValues & string, valores: unknown) => void;
  /** Al escribir: quita el error de ese campo. */
  limpiar: (campo: keyof TValues & string) => void;
  /** Al enviar: marca todos y devuelve los datos parseados, o `null`. */
  validar: (valores: unknown) => TValues | null;
  /** Borra todas las marcas (al cerrar un modal, al cambiar de pestaña). */
  reiniciar: () => void;
}

type Errores = Record<string, string>;

/**
 * El PRIMER problema de cada campo, no todos.
 *
 * Zod devuelve una incidencia por regla incumplida: un teléfono vacío
 * falla el largo y el formato a la vez. Enseñar las dos bajo la misma
 * caja no ayuda a nadie — se arregla la primera y la segunda suele
 * desaparecer sola—, así que gana la primera, que es la más básica.
 */
function primerErrorPorCampo(issues: readonly z.core.$ZodIssue[]): Errores {
  const out: Errores = {};
  for (const issue of issues) {
    const campo = issue.path.join(".");
    if (campo && !(campo in out)) out[campo] = issue.message;
  }
  return out;
}

export function useFieldErrors<TSchema extends z.ZodType>(
  schema: TSchema,
): FieldErrors<z.infer<TSchema>> {
  type TValues = z.infer<TSchema>;
  type Campo = keyof TValues & string;

  const [errors, setErrors] = useState<Errores>({});

  const revisar = useCallback(
    (campo: Campo, valores: unknown) => {
      const parsed = schema.safeParse(valores);
      const todos = parsed.success ? {} : primerErrorPorCampo(parsed.error.issues);
      setErrors((prev) => {
        const next = { ...prev };
        const problema = todos[campo];
        if (problema) next[campo] = problema;
        else delete next[campo];
        return next;
      });
    },
    [schema],
  );

  const limpiar = useCallback((campo: Campo) => {
    setErrors((prev) => {
      if (!(campo in prev)) return prev;
      const next = { ...prev };
      delete next[campo];
      return next;
    });
  }, []);

  const validar = useCallback(
    (valores: unknown): TValues | null => {
      const parsed = schema.safeParse(valores);
      if (parsed.success) {
        setErrors({});
        return parsed.data as TValues;
      }
      setErrors(primerErrorPorCampo(parsed.error.issues));
      return null;
    },
    [schema],
  );

  const reiniciar = useCallback(() => setErrors({}), []);

  return {
    errors: errors as Partial<Record<Campo, string>>,
    revisar,
    limpiar,
    validar,
    reiniciar,
  };
}
